/**
 * TRANSACT. UF_04, and the five step spine from UF_00 section 6.1.
 *
 *   1. COMPOSE     the user enters the details
 *   2. REVIEW      amount, fees, recipient, ESCROW PROTECTION, caps, any risk notice
 *   3. AUTHORIZE   PIN or biometric
 *   4. PROCESS     real-time status while the compliance and risk checks run and escrow locks
 *   5. RESULT      success, or a clear disclosable failure WITH A NEXT STEP
 *
 * ## Why review is a separate step and not a confirm dialog
 *
 * UF_00 section 3.3: "the user is never surprised. Fees, limits, risks, and locks are disclosed
 * before the user commits."
 *
 * A dialog that says "Send KES 5,000?" surprises nobody and informs nobody. This step exists to say
 * the things the participant cannot see: that the money will be held in escrow rather than sent
 * directly, that it comes out of their available balance the moment they authorize, and where it goes
 * if the recipient never appears.
 *
 * ## A BLOCKED movement is not an error
 *
 * The backend returns 200 with `outcome: 'blocked'` and a DISCLOSABLE reason. That is the platform
 * working, not failing: a cap was reached, a risk check held, a whitelist said no. It gets a
 * first-class result state that says what happened, confirms the money did not move, and offers a
 * next step — never a red "something went wrong", which tells a person nothing and frightens them
 * about money that is perfectly safe.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  api,
  LipaApiError,
  type Limits,
  type MovementResult,
  type Quote,
  type RateBoard,
} from '@/lib/api';
import { BlockedNotice } from '@/components/chrome';
import { CapHeadroom } from '@/components/patterns';
import { TierGate } from '@/components/gate';
import { PinField } from '@/components/auth-fields';
import { newIdempotencyKey } from '@/lib/ids';
import {
  decimalsFor,
  formatMoneyAmount,
  parseMoneyAmount,
  sanitizeAmountTyping,
} from '@/lib/money';
import {
  TILL_PATTERN,
  TillNotFoundError,
  looksLikeTill,
  normalizeTillNumber,
  tillService,
} from '@/lib/till';
import { useSession } from '@/lib/session-context';
import type { TillResolution } from '@/lib/api';

type Step = 'compose' | 'review' | 'authorize' | 'process' | 'result';

type Tab = 'send' | 'convert';

export default function Transact() {
  const [tab, setTab] = useState<Tab>('send');

  return (
    <TierGate requires="T2" what="Transacting">
      <div className="mx-auto max-w-md space-y-5">
        {/* Two things live under Transact: sending value, and converting between assets. A segmented
            control keeps them one tap apart without pushing Convert onto a route of its own. */}
        <div
          role="tablist"
          aria-label="Transact"
          className="grid grid-cols-2 gap-1 rounded-lg bg-canvas p-1"
        >
          {(['send', 'convert'] as const).map((t) => (
            <button
              key={t}
              role="tab"
              aria-selected={tab === t}
              onClick={() => setTab(t)}
              className={`rounded-md py-2 text-sm font-medium capitalize transition ${
                tab === t ? 'bg-surface text-ink shadow-sm' : 'text-ink-muted hover:text-ink'
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {tab === 'send' ? <Send /> : <Convert />}
      </div>
    </TierGate>
  );
}

function Send() {
  const { me } = useSession();

  const [step, setStep] = useState<Step>('compose');
  const [to, setTo] = useState('');
  const [amount, setAmount] = useState('');
  const [pin, setPin] = useState('');

  const [limits, setLimits] = useState<Limits | null>(null);
  const [result, setResult] = useState<MovementResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  /**
   * THE TILL, RESOLVED.
   *
   * "Who are you paying" now takes three kinds of thing: a VASP ID, a wallet address, or a
   * TILL (`LPUSDTTRC20KAHAWA`) -- the short handle a business prints on its counter, the way Lipa
   * na M-PESA works here. A till is an abstraction over a receiving wallet, and the only honest way
   * to use one is to resolve it and show the payer WHO they are about to pay before they commit.
   *
   * The handle NAMES ITS ASSET AND CHAIN, and that is not cosmetic: the app takes both from the
   * resolved record, so the payer chooses no network and cannot choose the wrong one. Sending USDT on
   * the wrong chain is the most common unrecoverable mistake in crypto payments, and a till is how
   * this app makes it unavailable rather than merely warned about.
   *
   * The resolution comes from the service, never from anything the payer typed beyond the till
   * number itself. That is the point of the indirection: if this screen accepted a wallet address
   * alongside a till and paid the address, a till would be decoration.
   */
  const [resolution, setResolution] = useState<TillResolution | null>(null);
  const [resolving, setResolving] = useState(false);
  const [tillError, setTillError] = useState<string | null>(null);

  const isTillAttempt = looksLikeTill(to);

  // THE ASSET FOLLOWS THE TILL. A USDT till is paid in USDT; everything else on this screen is KES.
  // A till that quietly received shillings would be the one mistake this feature exists to prevent.
  const asset = resolution?.asset ?? 'KES';
  const parsedAmount = parseMoneyAmount(amount, decimalsFor(asset));

  useEffect(() => {
    const typed = to.trim();
    if (!looksLikeTill(typed)) {
      setResolution(null);
      setTillError(null);
      return;
    }

    const canonical = normalizeTillNumber(typed);
    // Mid-typing is not an error. `LPUSDTTR` is a person half way through a handle, not a wrong one,
    // so nothing is said until the shape is complete. Where the segments begin and end is the
    // RECORD's business, not this screen's: all that is checked here is that enough has been typed
    // to be worth asking about.
    if (!TILL_PATTERN.test(canonical)) {
      setResolution(null);
      setTillError(null);
      return;
    }

    let live = true;
    setResolving(true);
    const timer = setTimeout(async () => {
      try {
        const resolved = await tillService.resolveTill(canonical);
        if (!live) return;
        setResolution(resolved);
        setTillError(null);
      } catch (e) {
        if (!live) return;
        setResolution(null);
        setTillError(
          e instanceof TillNotFoundError
            ? 'We could not find that Till. Check the number and try again.'
            : 'We could not check that Till just now. Try again in a moment.',
        );
      } finally {
        if (live) setResolving(false);
      }
    }, 300);

    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [to]);

  /**
   * The headroom, fetched BEFORE the user composes. UF_00 section 6.3.
   *
   * A cap discovered at the moment of payment is a cap that made somebody look foolish in front of
   * whoever they were paying, and the platform knew the answer all along.
   *
   * It is a SNAPSHOT and it reserves nothing. By the time Send is pressed it may be stale — another
   * tab, a movement that settled — and that is fine, because the authoritative check runs inside the
   * movement under a row lock. This is a courtesy; `reserve_headroom` is the control.
   */
  const loadLimits = useCallback(async () => {
    if (!me) return;
    try {
      setLimits(await api.limits(me.participant_id));
    } catch {
      // A headroom we could not fetch is not a reason to stop somebody paying their rent. The cap is
      // enforced on the movement regardless, so the honest thing is to show no widget rather than to
      // block the screen over a nicety.
      setLimits(null);
    }
  }, [me]);

  useEffect(() => {
    void loadLimits();
  }, [loadLimits]);

  /**
   * The idempotency key is generated ONCE, when the user commits, and reused on any retry.
   *
   * A fresh key per attempt would defeat the whole mechanism: two attempts with two keys are two
   * different payments as far as the backend is concerned, and the participant is debited twice. This
   * is the single most important line in the file.
   */
  const [idempotencyKey, setIdempotencyKey] = useState<string | null>(null);

  async function authorize(e: React.FormEvent) {
    e.preventDefault();
    if (!me) return;

    const key = idempotencyKey ?? newIdempotencyKey();
    setIdempotencyKey(key);

    setStep('process');
    setError(null);

    try {
      // A till pays the participant the SERVICE named, never the string in the box. The box held
      // "LP-KAHAWA"; the money goes to whoever that resolved to.
      const recipient = resolution?.recipient_id ?? to.trim();
      const exact = parsedAmount.ok ? parsedAmount.normalized : amount;
      const r = await api.send(me.participant_id, recipient, exact, asset, key, pin);
      setResult(r);
      setStep('result');
    } catch (e) {
      setError(
        e instanceof LipaApiError
          ? e.detail.message
          : 'We could not reach the platform. Your money has not moved.',
      );
      setStep('result');
    }
  }

  function startAgain() {
    setStep('compose');
    setTo('');
    setAmount('');
    setPin('');
    setResult(null);
    setError(null);
    setResolution(null);
    setTillError(null);
    setIdempotencyKey(null); // a NEW payment, so a new key. Only a RETRY reuses one.

    // The money just moved, so the headroom just changed. Showing the old bar would be the exact lie
    // this widget exists to prevent, and it would be a lie the user watched us tell.
    void loadLimits();
  }

  /**
   * Which cap this payment would breach, in the participant's words. Null if none.
   *
   * EXACT INTEGER ARITHMETIC, in minor units. `Number('1234.56') * 100` is 123456.00000000001 on some
   * inputs, and a limit check that is wrong by a hundredth of a cent is a limit check that sometimes
   * says the wrong thing for reasons nobody can reproduce.
   */
  const breach = (() => {
    if (!limits?.available || !amount) return null;

    // The headroom endpoint answers in ONE asset (`limits.asset`, KES today). A USDT payment to a
    // till is not measured by a shilling cap, and comparing the two numbers would be comparing
    // 10 USDT against a KES limit as though the units matched. The authoritative check runs in the
    // movement for every asset regardless; this widget simply has nothing to say about that one.
    if (asset !== limits.asset) return null;

    if (!parsedAmount.ok) return null; // not a number yet; the input's own validation will say so
    const minor = parsedAmount.minor;

    if (minor > BigInt(limits.per_transaction.limit)) {
      return `The most you can send in one payment is ${limits.asset} ${formatMoneyAmount(
        limits.per_transaction.limit,
      )}. Try sending it in more than one payment.`;
    }

    if (minor > BigInt(limits.daily.remaining)) {
      return `You have ${limits.asset} ${formatMoneyAmount(limits.daily.remaining)} left of your daily limit today. It resets at midnight.`;
    }

    if (minor > BigInt(limits.cumulative.remaining)) {
      // Cumulative does NOT reset. Telling somebody to come back tomorrow would be sending them away
      // for nothing.
      return `This is over your total limit for the sandbox. Contact support to have it reviewed.`;
    }

    return null;
  })();

  return (
    <div className="mx-auto max-w-md space-y-5">
      <h1 className="text-page-title text-ink">Send money</h1>

      {/*
        THE HEADROOM, BEFORE THE USER TYPES A NUMBER. Not after they are refused.

        Amber at 80 percent, never red: a cap being approached is not an error, the participant has
        done nothing wrong, and colouring it red teaches people that red means nothing.
      */}
      {/*
        THE HEADROOM IS ONLY SHOWN FOR THE ASSET IT GOVERNS.

        `/limits` answers in one asset (KES today). Rendering a shilling cap above a USDT payment to a
        till puts a number on screen that looks like it applies and does not, which is the same
        failure as a stale cap: the participant plans around a figure that was never about them. Caps
        DO apply to the other asset -- they are enforced in the movement -- so the honest thing is to
        say we cannot show them here rather than to show the wrong ones.
      */}
      {step === 'compose' && limits?.available && asset !== limits.asset ? (
        <p className="rounded-card bg-surface-sunken p-3 text-xs text-ink-muted">
          Your {limits.asset} limits are not what governs a {asset} payment, so they are not shown
          here. Your limits still apply and are checked when you authorize.
        </p>
      ) : null}

      {step === 'compose' && limits?.available && asset === limits.asset ? (
        <div className="space-y-3">
          <CapHeadroom
            label="Your daily limit"
            used={limits.daily.used}
            limit={limits.daily.limit}
            asset={limits.asset}
            resets={new Date(limits.daily.resets_at).toLocaleString(undefined, {
              hour: 'numeric',
              minute: '2-digit',
            })}
          />
          <p className="text-center text-xs text-ink-muted">
            Most you can send in one go: {limits.asset}{' '}
            {formatMoneyAmount(limits.per_transaction.limit)}
          </p>
        </div>
      ) : null}

      {/* Caps FAIL CLOSED. An unconfigured cap means every movement is refused, so saying nothing —
          or worse, showing an empty progress bar that reads as "plenty left" — would set the user up
          to be blocked with no explanation. */}
      {step === 'compose' && limits && !limits.available ? (
        <div className="rounded-card border border-warning/30 bg-warning/5 p-4 text-sm">
          <p className="font-medium text-ink">Sending is unavailable right now</p>
          <p className="mt-1 text-ink-muted">
            The platform&apos;s limits are not configured for this asset, so no payment can go
            through. We are aware of it. Nothing is wrong with your account or your money.
          </p>
        </div>
      ) : null}

      {step === 'compose' ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            // A till that has not resolved is not a recipient. Blocking here is the difference
            // between paying a business and paying a string.
            if (isTillAttempt && !resolution) return;
            if (!parsedAmount.ok) return;
            setStep('review');
          }}
          className="space-y-4 rounded-card border border-border bg-surface p-5"
        >
          <div>
            <label htmlFor="to" className="block text-sm font-medium text-ink">
              Who are you paying?
            </label>
            <input
              id="to"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="VASP ID, wallet address or Till"
              aria-invalid={!!tillError}
              aria-describedby="to-help"
              required
              className="mt-1 w-full rounded-lg border border-border bg-surface px-3 py-2 font-mono text-sm text-ink placeholder:text-ink-muted outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            />
            <p id="to-help" className="mt-1 text-xs text-ink-muted">
              A VASP ID, a wallet address, or a Till like{' '}
              <span className="font-mono">LPUSDTTRC20KAHAWA</span>. A Till names the asset and the
              network it accepts. Whoever you pay must be verified on VASP.
            </p>

            {/* THE RECIPIENT, NAMED, BEFORE ANYTHING IS COMMITTED. */}
            {resolving ? (
              <p className="mt-2 text-xs text-ink-subtle">Checking that Till&hellip;</p>
            ) : null}

            {tillError ? (
              <p role="alert" className="mt-2 text-xs text-danger">
                {tillError}
              </p>
            ) : null}

            {resolution ? (
              <div className="mt-2 rounded-card border border-primary/30 bg-primary-tint p-3">
                <p className="text-xs font-medium uppercase tracking-wide text-brand">
                  Payment recipient
                </p>
                <dl className="mt-2 space-y-1.5 text-sm">
                  <Row label="Till" value={resolution.till_number} mono />
                  <Row label="Recipient" value={resolution.recipient_name} />
                  <Row label="Asset" value={resolution.asset} />
                  <Row
                    label="Network"
                    value={`${resolution.network} (${resolution.network_tag})`}
                  />
                </dl>
                <p className="mt-2 text-xs text-ink-muted">
                  Paying a Till sends {resolution.asset} to the wallet behind it, on{' '}
                  {resolution.network}. You never need the address, and there is no network to
                  choose: the Till already names it.
                </p>
              </div>
            ) : null}
          </div>

          <div>
            <label htmlFor="amount" className="block text-sm font-medium text-ink">
              How much?
            </label>
            <div className="mt-1 flex items-center gap-2">
              {/* The asset follows the recipient: a USDT till is paid in USDT. */}
              <span className="text-sm text-ink-muted">{asset}</span>
              <input
                id="amount"
                inputMode="decimal"
                value={amount}
                // A DECIMAL STRING, all the way to the backend. The moment an amount becomes a
                // JavaScript number it becomes an IEEE-754 double, and 0.1 is not exactly
                // representable in one. This is somebody's money.
                onChange={(e) => setAmount(sanitizeAmountTyping(e.target.value))}
                placeholder="0.00"
                required
                className="w-full rounded-lg border border-border bg-surface px-3 py-2 font-mono text-sm text-ink placeholder:text-ink-muted outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              />
            </div>
            {amount && !parsedAmount.ok ? (
              <p className="mt-1 text-xs text-danger">{parsedAmount.reason}</p>
            ) : null}
          </div>

          <button
            type="submit"
            disabled={(isTillAttempt && !resolution) || !amount || !parsedAmount.ok}
            className="w-full rounded-lg bg-primary py-2.5 text-sm font-medium text-white transition hover:bg-primary-light disabled:cursor-not-allowed disabled:opacity-50"
          >
            Review
          </button>
        </form>
      ) : null}

      {step === 'review' ? (
        <div className="space-y-4 rounded-card border border-border bg-surface p-5">
          <h2 className="font-medium text-ink">Check this before you commit</h2>

          <dl className="space-y-2 text-sm">
            <Row label="Amount" value={`${asset} ${amount}`} />
            <Row label="Fee" value={`${asset} 0.00`} />
            {/* A till is shown as BOTH: the handle they typed, and the recipient it resolved to.
                Showing only the handle would ask somebody to confirm a payment to a string. */}
            {resolution ? (
              <>
                <Row label="Till" value={resolution.till_number} mono />
                <Row label="Recipient" value={resolution.recipient_name} />
                <Row label="Network" value={`${resolution.network} (${resolution.network_tag})`} />
              </>
            ) : (
              <Row label="To" value={to} mono />
            )}
          </dl>

          {/* THE ESCROW DISCLOSURE. This is the thing the participant cannot see, and the whole
              reason this step exists. */}
          <div className="rounded-lg bg-canvas p-3 text-sm leading-relaxed text-ink-muted">
            <p className="font-medium text-ink">Your money is held in escrow first.</p>
            <p className="mt-1">
              It leaves your available balance the moment you authorize, and it is held — not sent —
              while the checks run. If anything stops this, it comes straight back to you.
            </p>
          </div>

          {/*
            THE CAP WARNING, BEFORE THEY AUTHORIZE.

            The backend will refuse this. Saying so now — rather than letting them enter their PIN,
            watch a spinner, and then be blocked — is the difference between a limit and an ambush.
            The button is still enabled, on purpose: the check that matters runs in the movement, and
            a UI that disables it here would be claiming an authority it does not have if this
            snapshot were stale.
          */}
          {breach ? (
            <div className="rounded-lg border border-warning/30 bg-warning/5 p-3 text-sm">
              <p className="font-medium text-ink">This will be refused</p>
              <p className="mt-1 text-ink-muted">{breach}</p>
            </div>
          ) : null}

          <div className="flex gap-3">
            <button
              onClick={() => setStep('compose')}
              className="flex-1 rounded-lg border border-border py-2.5 text-sm font-medium text-ink transition hover:bg-canvas"
            >
              Back
            </button>
            <button
              onClick={() => setStep('authorize')}
              className="flex-1 rounded-lg bg-primary py-2.5 text-sm font-medium text-white transition hover:bg-primary-light"
            >
              Continue
            </button>
          </div>
        </div>
      ) : null}

      {step === 'authorize' ? (
        <form
          onSubmit={authorize}
          className="space-y-4 rounded-card border border-border bg-surface p-5"
        >
          <h2 className="font-medium text-ink">Enter your PIN to authorize</h2>
          <p className="text-sm text-ink-muted">
            Sending {asset} {amount}. This is the last step before the money leaves your balance.
          </p>

          {/* The SAME PIN entry the rest of the app uses (sign-in, onboarding): four masked boxes, not
              a bespoke text input, so the interaction is identical everywhere a PIN is asked for. */}
          <div className="flex justify-center">
            <PinField value={pin} onChange={setPin} length={4} autoFocus ariaLabel="PIN" />
          </div>

          {/*
            The PIN is now a REAL step-up: it is sent with the movement and the backend verifies it
            against the account's stored PIN hash before anything is debited. A wrong PIN comes back as
            outcome 'blocked' at stage 'authorize' and no money moves. (The demo account has no fixed
            PIN and accepts any 4-digit PIN — it is a tour account, not a real user.)
          */}
          <p className="text-center text-xs text-ink-muted">
            Your PIN authorizes this payment. Enter it wrong and nothing moves.
          </p>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setStep('review')}
              className="flex-1 rounded-lg border border-border py-2.5 text-sm font-medium text-ink transition hover:bg-canvas"
            >
              Back
            </button>
            <button
              type="submit"
              disabled={pin.length < 4}
              className="flex-1 rounded-lg bg-primary py-2.5 text-sm font-medium text-white transition hover:bg-primary-light disabled:opacity-50"
            >
              Authorize
            </button>
          </div>
        </form>
      ) : null}

      {step === 'process' ? (
        <div className="rounded-card border border-border bg-surface p-8 text-center">
          <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-border border-t-primary" />
          <p className="mt-4 text-sm font-medium text-ink">Running the checks</p>
          <p className="mt-1 text-sm text-ink-muted">
            Verifying you, checking your limits, scoring the risk, and holding the money in escrow.
            Do not close this.
          </p>
        </div>
      ) : null}

      {step === 'result' ? (
        <div className="space-y-4">
          {error ? (
            <BlockedNotice reason={error} />
          ) : result?.outcome === 'settled' ? (
            <div className="rounded-card border border-accent/30 bg-accent/5 p-6 text-center">
              <p className="font-semibold text-accent">Sent</p>
              <p className="mt-1 text-sm text-ink-muted">
                {asset} {amount} has been released from escrow and paid.
              </p>

              {/* Straight to the full trail. FR-U-TXN-007: the participant can see every check that
                  ran on their payment, in order, rather than taking our word for it. */}
              <Link
                to={`/activity/${result.correlation_id}`}
                className="mt-3 inline-block text-sm text-brand hover:underline"
              >
                See what happened, step by step
              </Link>
            </div>
          ) : result?.outcome === 'blocked' ? (
            <>
              {/* NOT an error. A control did its job. The participant is told, in words they can act
                  on, and their money is where it was. */}
              <BlockedNotice reason={result.disclosable_reason} stage={result.stage} />

              {/*
                UF_00 section 6.5: a blocked state "explains the gate and the NEXT STEP".

                This used to say a dispute would be possible "once the support panel is built", which
                was true and was not good enough: a person whose payment has just been refused was
                being told to come back later. The panel exists, and the refusal carries its own
                reference into it — so the participant does not have to go and find it, and an officer
                does not have to ask them for it.
              */}
              <Link
                to={`/support?about=${result.correlation_id}`}
                className="block rounded-card border border-border bg-surface p-4 text-center text-sm text-brand transition hover:bg-canvas"
              >
                Think this is wrong? Tell us, and we will investigate
              </Link>
            </>
          ) : result?.outcome === 'rolled_back' ? (
            <BlockedNotice
              reason="This did not complete, so we returned your money. Nothing was sent."
              stage={result.stage}
            />
          ) : null}

          <button
            onClick={startAgain}
            className="w-full rounded-lg border border-border bg-surface py-2.5 text-sm font-medium text-ink transition hover:bg-canvas"
          >
            Done
          </button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Minor units to a display string. Integer arithmetic only, via BigInt.
 *
 * A daily cap in minor units can exceed 2^53, and the one number in this file that must never be
 * approximate is the one that decides whether somebody's rent payment goes through. `Number(...)`
 * here would be a silent rounding error in the participant's limit.
 */
// =============================================================================
// CONVERT (UF-04, FR-U-TXN-003, FR-U-TXN-004).
//
// A rate board and a quote calculator. It shows a participant what a conversion would look like; it
// does NOT execute one. Convert execution (FR-X-TXN-004) is a later sandbox phase, settles atomically
// against real liquidity, and re-quotes at that moment. The screen says so, rather than dressing a
// display feed up as a button that moves money.
// =============================================================================

const CONVERT_ASSETS = ['KES', 'BTC', 'ETH', 'USDT', 'USDC', 'SOL', 'XRP', 'BNB'] as const;

function Convert() {
  const [board, setBoard] = useState<RateBoard | null>(null);
  const [boardError, setBoardError] = useState(false);
  const [from, setFrom] = useState('BTC');
  const [to, setTo] = useState('KES');
  const [amount, setAmount] = useState('');
  const [quote, setQuote] = useState<Quote | null>(null);
  const [quoting, setQuoting] = useState(false);
  const [quoteError, setQuoteError] = useState<string | null>(null);

  const loadBoard = useCallback(async () => {
    try {
      setBoard(await api.rates());
      setBoardError(false);
    } catch {
      setBoardError(true);
    }
  }, []);

  useEffect(() => {
    void loadBoard();
  }, [loadBoard]);

  /**
   * The quote, DEBOUNCED. A request per keystroke would hammer the endpoint and flicker the number; a
   * short settle lets the participant finish typing first. The quote reserves nothing, so re-fetching
   * freely is harmless.
   */
  useEffect(() => {
    if (!amount || from === to || !/^\d+(\.\d+)?$/.test(amount)) {
      setQuote(null);
      setQuoteError(null);
      return;
    }
    setQuoting(true);
    const timer = setTimeout(async () => {
      try {
        setQuote(await api.quote(from, to, amount));
        setQuoteError(null);
      } catch (e) {
        setQuote(null);
        setQuoteError(
          e instanceof LipaApiError ? e.detail.message : 'We could not fetch a quote just now.',
        );
      } finally {
        setQuoting(false);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [from, to, amount]);

  function swap() {
    setFrom(to);
    setTo(from);
    setQuote(null);
  }

  const crypto = (board?.rates ?? []).filter((r) => r.asset !== board?.numeraire);

  return (
    <div className="space-y-5">
      <h1 className="text-page-title text-ink">Convert</h1>

      {/* THE RATE BOARD */}
      <div className="rounded-card border border-border bg-surface p-5">
        <div className="flex items-center justify-between">
          <h2 className="font-medium text-ink">Today&apos;s rates</h2>
          {board ? <SourceBadge live={board.live} /> : null}
        </div>

        {boardError ? (
          <p className="mt-3 text-sm text-ink-muted">
            We could not load rates just now.{' '}
            <button onClick={() => void loadBoard()} className="text-brand underline">
              Try again
            </button>
            .
          </p>
        ) : !board ? (
          <p className="mt-3 text-sm text-ink-muted">Loading rates&hellip;</p>
        ) : (
          <>
            <dl className="mt-3 space-y-2 text-sm">
              {crypto.map((r) => (
                <div key={r.asset} className="flex items-baseline justify-between">
                  <dt className="text-ink-muted">1 {r.asset}</dt>
                  <dd className="tabular font-medium text-ink">
                    {board.numeraire} {fmtRate(r.kes_per_unit)}
                  </dd>
                </div>
              ))}
            </dl>
            <p className="mt-3 text-xs text-ink-muted">
              {board.live
                ? `Live rates, updated ${relTime(board.as_of)}.`
                : 'Indicative rates. Not a live market quote.'}
            </p>
          </>
        )}
      </div>

      {/* THE CONVERTER */}
      <div className="space-y-4 rounded-card border border-border bg-surface p-5">
        <div>
          <label className="block text-sm font-medium text-ink" htmlFor="conv-amount">
            You convert
          </label>
          <div className="mt-1 flex gap-2">
            <input
              id="conv-amount"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))}
              placeholder="0.00"
              className="w-full rounded-lg border border-border bg-surface px-3 py-2 font-mono text-sm text-ink placeholder:text-ink-muted outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            />
            <AssetSelect value={from} onChange={setFrom} exclude={to} />
          </div>
        </div>

        <div className="flex justify-center">
          <button
            onClick={swap}
            aria-label="Swap the two assets"
            className="rounded-full border border-border bg-canvas p-2 text-ink-muted transition hover:text-brand"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M7 4v16M7 4L4 7M7 4l3 3M17 20V4M17 20l3-3M17 20l-3-3" />
            </svg>
          </button>
        </div>

        <div>
          <label className="block text-sm font-medium text-ink" htmlFor="conv-get">
            You get
          </label>
          <div className="mt-1 flex gap-2">
            <div
              id="conv-get"
              className="flex w-full items-center rounded-lg border border-border bg-canvas px-3 py-2 font-mono text-sm text-ink"
            >
              {quoting ? (
                <span className="text-ink-muted">&hellip;</span>
              ) : quote ? (
                <span className="tabular">{fmtAmount(quote.receive.display, quote.to)}</span>
              ) : (
                <span className="text-ink-muted">0.00</span>
              )}
            </div>
            <AssetSelect value={to} onChange={setTo} exclude={from} />
          </div>
        </div>

        {quoteError ? <p className="text-sm text-warning">{quoteError}</p> : null}

        {quote ? (
          <dl className="space-y-2 rounded-lg bg-canvas p-3 text-sm">
            <Row label="Rate" value={`1 ${quote.from} = ${fmtRate(quote.rate)} ${quote.to}`} />
            <Row
              label={`Fee (${(quote.fee_bps / 100).toFixed(2)}%)`}
              value={`${fmtAmount(quote.fee.display, quote.to)} ${quote.to}`}
            />
            <Row
              label="You get"
              value={`${fmtAmount(quote.receive.display, quote.to)} ${quote.to}`}
            />
          </dl>
        ) : null}

        {/* HONEST ABOUT THE CONTROL WE DO NOT YET HAVE. The PIN box on Send is a friction step; this
            is a quote with no execute button at all, because there is no convert pipeline behind it
            yet, and a button that did nothing would be worse than none. */}
        <div className="rounded-lg bg-canvas p-3 text-xs leading-relaxed text-ink-muted">
          This is an indicative quote. Converting your balance on VASP activates in a later
          sandbox phase, and when it does the rate is confirmed again at the moment of exchange.
        </div>
      </div>
    </div>
  );
}

function SourceBadge({ live }: { live: boolean }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ${
        live ? 'bg-primary/10 text-brand' : 'bg-canvas text-ink-muted'
      }`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${live ? 'bg-primary' : 'bg-ink-muted'}`} />
      {live ? 'Live' : 'Indicative'}
    </span>
  );
}

function AssetSelect({
  value,
  onChange,
  exclude,
}: {
  value: string;
  onChange: (v: string) => void;
  exclude?: string;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label="Asset"
      className="rounded-lg border border-border bg-surface px-3 py-2 text-sm font-medium text-ink outline-none focus:border-primary focus:ring-1 focus:ring-primary"
    >
      {CONVERT_ASSETS.map((a) => (
        <option key={a} value={a} disabled={a === exclude}>
          {a}
        </option>
      ))}
    </select>
  );
}

/** A KES-per-unit or cross rate, grouped and sized to its magnitude. Display only. */
function fmtRate(s: string): string {
  const n = Number(s);
  if (!isFinite(n)) return s;
  if (n === 0) return '0';
  if (n >= 1000) return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
  if (n >= 1) return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
  return n.toLocaleString(undefined, { maximumFractionDigits: 8 });
}

/**
 * A converted amount for display. Fiat shows 2 places; a virtual asset shows up to 8. This goes
 * through `Number`, which is fine HERE and nowhere near the value path: it is an indicative quote, and
 * the exact minor units are still carried in `quote.receive.amount` if anything ever needs them.
 */
function fmtAmount(display: string, asset: string): string {
  const n = Number(display);
  if (!isFinite(n)) return display;
  return asset === 'KES'
    ? n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : n.toLocaleString(undefined, { maximumFractionDigits: 8 });
}

function relTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (isNaN(then)) return 'just now';
  const secs = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (secs < 60) return 'moments ago';
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins} min ago`;
  return `${Math.round(mins / 60)} h ago`;
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-ink-muted">{label}</dt>
      <dd className={`truncate font-medium text-ink ${mono ? 'font-mono text-xs' : 'tabular'}`}>
        {value}
      </dd>
    </div>
  );
}
